import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import { questionTypes } from "../../../../constants";
import EditQuestionModal from "../../../../components/QuestionBank/EditQuestionModal";
import { filesPreloading } from "../../../../services/fileUploadService";
import { isEmpty, every } from "lodash";
import {
  getDefaultAnswerChoices,
  getDefaultMatches
} from "../../../../utils/courseBuilderUtils";
import { resourceObjectTypes } from "../../../../constants/index";
import LoadingSpinnerWithBackdrop from "../../../../components/LoadingSpinner/LoadingSpinnerWithBackdrop";
import { enqueueSuccessSnackbar } from "../../../../redux/actions/snackbarActions";
import { useDispatch } from "react-redux";

const {
  fillInTheBlankAnswerType,
  fillInTheBlankTextType
} = resourceObjectTypes;

const questionTypeValues = Object.values(questionTypes);

// TODO: do translations outside of schema instead of within the validation schema
const getCreateQuizOrSurveySchema = (t, isSurveyQuestion) => {
  return Yup.object().shape({
    text: Yup.string().when("questionType", {
      is: questionType => questionType !== questionTypes.fillBlanks,
      then: Yup.string().required(t("validationMessages.questionTextRequired"))
    }),
    points: Yup.number()
      .required(t("validationMessages.validNumber"))
      .min(1, t("validationMessages.validNumber"))
      .integer(t("validationMessages.mustBeInteger")),
    answers: Yup.array()
      .when("questionType", {
        is: questionType =>
          questionType === questionTypes.multipleChoice ||
          questionType === questionTypes.ordering ||
          questionType === questionTypes.multiSelect,
        then: Yup.array()
          .min(2, t("validationMessages.moreThanOneAnswer"))
          .test(
            "noEmptyAnswers",
            t("validationMessages.noEmptyAnswers"),
            function() {
              return this.parent.answers.every(({ text }) => !!text);
            }
          )
      })
      .when("questionType", {
        is: questionType =>
          questionType === questionTypes.multiSelect && !isSurveyQuestion,
        then: Yup.array().test(
          "multiSelect",
          t("validationMessages.mustHaveAtLeastTwoCorrectAnswers"),
          function() {
            return (
              this.parent.answers.filter(({ correct }) => !!correct).length > 1
            );
          }
        )
      })
      .when("questionType", {
        is: questionType =>
          questionType === questionTypes.multipleChoice && !isSurveyQuestion,
        then: Yup.array().test(
          "multipleChoice",
          t("validationMessages.mustHaveAtLeastOneCorrectAnswers"),
          function() {
            return (
              this.parent.answers.filter(({ correct }) => !!correct).length >= 1
            );
          }
        )
      })
      .when("questionType", {
        // We only need short answer validation for quiz questions, not survey questions.
        is: questionType =>
          questionType === questionTypes.shortAnswer && !isSurveyQuestion,
        then: Yup.array()
          .test(
            "answers",
            t("validationMessages.noEmptyShortAnswers"),
            function() {
              return every(this.parent.answers, answer => !!answer.text);
            }
          )
          .min(1, t("validationMessages.atLeastOneAnswer"))
      }),
    matchClues: Yup.array().when("questionType", {
      is: questionType => questionType === questionTypes.matching,
      then: Yup.array().of(
        Yup.object().shape({
          matchClue: Yup.string()
            .test(
              "matchClue",
              t("validationMessages.matchCannotBeBlank"),
              function() {
                return !!this.parent.text;
              }
            )
            .test(
              "matchClue",
              t("validationMessages.mustHaveCorrectMatch"),
              function() {
                return (
                  isSurveyQuestion || !!this.parent.correctMatch // survey questions don't need correct matches
                );
              }
            )
        })
      )
    }),
    matchChoices: Yup.array().when("questionType", {
      is: questionType => questionType === questionTypes.matching,
      then: Yup.array().of(
        Yup.object().shape({
          matchChoice: Yup.string().test(
            "matchChoice",
            t("validationMessages.choiceCannotBeBlank"),
            function() {
              return !!this.parent.text;
            }
          )
        })
      )
    }),
    fillInTheBlanks: Yup.array().when("questionType", {
      is: questionTypes.fillBlanks,
      then: Yup.array()
        .test(
          "fillInTheBlanks",
          t("validationMessages.atLeastOneBlankAndText"),
          function() {
            return (
              this.parent.fillInTheBlanks &&
              this.parent.fillInTheBlanks.some(
                ({ type }) => type === fillInTheBlankAnswerType
              ) &&
              this.parent.fillInTheBlanks.some(
                ({ type }) => type === fillInTheBlankTextType
              )
            );
          }
        )
        .test(
          "fillInTheBlanks",
          isSurveyQuestion
            ? t("validationMessages.noEmptyTexts")
            : t("validationMessages.noEmptyBlanksOrTexts"),
          function() {
            if (isSurveyQuestion) {
              return (
                this.parent.fillInTheBlanks &&
                this.parent.fillInTheBlanks.every(({ type, text }) => {
                  return type === "fibAnswer" ? true : !!text;
                })
              );
            } else {
              return (
                this.parent.fillInTheBlanks &&
                this.parent.fillInTheBlanks.every(({ text }) => !!text)
              );
            }
          }
        )
    })
  });
};

const EditQuestion = ({
  onClose,
  open,
  onSaveQuestion,
  newQuestionType,
  loading,
  existingQuestionData,
  isSurveyQuestion,
  quizId,
  surveyId
}) => {
  const { t } = useTranslation();

  const [questionType, setQuestionType] = useState(questionTypes.trueFalse);
  const [filesList, setFilterList] = useState([]);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const dispatch = useDispatch();

  const defaultAnswerChoices = getDefaultAnswerChoices(
    questionType,
    isSurveyQuestion,
    t
  );

  useEffect(() => {
    setQuestionType(newQuestionType);
  }, [newQuestionType]);

  const processPreloadingMedia = async newQuestionData => {
    const tmpObj = await filesPreloading(filesList, newQuestionData.text);
    newQuestionData.images = tmpObj.images;
    newQuestionData.media = tmpObj.media;
    newQuestionData.attachments = tmpObj.attachments;
    newQuestionData.text = tmpObj.content;
    if (newQuestionData.questionType === "matching") {
      for (const matchClue of newQuestionData.matchClues) {
        const tmpmatchClueObj = await filesPreloading(
          filesList,
          matchClue.text
        );
        matchClue.images = tmpmatchClueObj.images;
        matchClue.media = tmpmatchClueObj.media;
        matchClue.attachments = tmpmatchClueObj.attachments;
        matchClue.text = tmpmatchClueObj.content;
      }
      for (const matchChoice of newQuestionData.matchChoices) {
        const tmpmatchChoiceObj = await filesPreloading(
          filesList,
          matchChoice.text
        );
        matchChoice.images = tmpmatchChoiceObj.images;
        matchChoice.media = tmpmatchChoiceObj.media;
        matchChoice.attachments = tmpmatchChoiceObj.attachments;
        matchChoice.text = tmpmatchChoiceObj.content;
      }
    } else {
      for (const answer of newQuestionData.answers) {
        const tmpAnswerObj = await filesPreloading(filesList, answer.text);
        answer.images = tmpAnswerObj.images;
        answer.media = tmpAnswerObj.media;
        answer.attachments = tmpAnswerObj.attachments;
        answer.text = tmpAnswerObj.content;
      }
    }
    return newQuestionData;
  };

  const showNotificationMessage = () => {
    dispatch(
      enqueueSuccessSnackbar({
        message:
          "Oops! Two answer blanks must be separated by a text field. We'll combine the contiguous answer blanks into one blank."
      })
    );
  };

  const handleSaveQuestion = async (newQuestionData, resetForm) => {
    setShowLoadingScreen(true);
    const fillInTheBlanks = newQuestionData.fillInTheBlanks;
    let notificationMessage = false;
    if (fillInTheBlanks) {
      for (var i = fillInTheBlanks.length - 1; i >= 0; i--) {
        if (fillInTheBlanks[i - 1]) {
          if (
            fillInTheBlanks[i].type === fillInTheBlanks[i - 1].type &&
            fillInTheBlanks[i].type === fillInTheBlankAnswerType
          ) {
            fillInTheBlanks[i - 1].text =
              fillInTheBlanks[i - 1].text + " " + fillInTheBlanks[i].text;
            fillInTheBlanks.splice(i, 1);
            notificationMessage = true;
          }
        }
      }
    }
    newQuestionData.fillInTheBlanks = fillInTheBlanks;
    const {
      saveAndCreateNewQuestionType,
      ...questionData
    } = await processPreloadingMedia(newQuestionData);
    setShowLoadingScreen(false);
    const questionDataToSave = {
      ...questionData,
      courseQuizId: quizId || surveyId
    };
    onSaveQuestion(
      {
        type: "question",
        relationshipNames: [
          "answers",
          "matchClues",
          "matchChoices",
          "fillInTheBlanks",
          "fibElements",
          "fibAnswers"
        ],
        ...questionDataToSave
      },
      saveAndCreateNewQuestionType,
      existingQuestionData ? existingQuestionData.id : null,
      resetForm
    );
    if (notificationMessage) {
      showNotificationMessage();
    }
  };

  return (
    <Formik
      initialValues={{
        saveAndCreateNewQuestionType: "",
        questionType,
        text: existingQuestionData ? existingQuestionData.text : "",
        mandatory: existingQuestionData
          ? existingQuestionData.mandatory
          : false,
        points: existingQuestionData ? existingQuestionData.points : 1,
        answers: existingQuestionData
          ? sortBy(existingQuestionData.answerChoices, ["order"])
          : defaultAnswerChoices,
        ...((questionType === questionTypes.multipleChoice ||
          questionType === questionTypes.multiSelect) && {
          randomizeAnswers: existingQuestionData
            ? existingQuestionData.randomizeAnswers
            : false
        }),
        ...(questionType === questionTypes.fillBlanks && {
          fillInTheBlanks: existingQuestionData
            ? sortBy(
                [].concat(
                  existingQuestionData.fibAnswers,
                  existingQuestionData.fibElements
                ),
                ["position"]
              )
            : []
        }),
        // Scoring behavior not necessary for MVP
        /* ...(questionType === questionTypes.ordering && {
          scoringBehavior: quizQuestionsScoringBehavior.allOrNothing
        }), */
        ...(questionType === questionTypes.longAnswer && {
          additionalOptions: {
            allowHtml: existingQuestionData?.options
              ? existingQuestionData.options.allowHtml
              : true
          }
        }),
        ...(questionType === questionTypes.matching && {
          ...(existingQuestionData
            ? {
                matchClues: existingQuestionData.matchClues,
                matchChoices: existingQuestionData.matchChoices
              }
            : getDefaultMatches(isSurveyQuestion))
        })
      }}
      validationSchema={getCreateQuizOrSurveySchema(t, isSurveyQuestion)}
      enableReinitialize
      onSubmit={(values, { resetForm }) =>
        handleSaveQuestion(values, resetForm)
      }
    >
      {({
        setFieldValue,
        validateForm,
        submitForm,
        values,
        setFieldTouched
      }) => (
        <Form data-qa="edit-question-form">
          <EditQuestionModal
            isNewQuestion={!existingQuestionData}
            onSubmit={() => {
              validateForm().then(errors => {
                if (
                  !isEmpty(errors) &&
                  values.questionType === questionTypes.matching
                )
                  /** When the user "touches" the matchChoice field by editing it, we can't create
                      a new object or otherwise we'll get an error because the "correctChoice" match choice
                      for the matchClue won't have reference equality to the matchChoice. Therefore Formik won't
                      mark the field as touched so we need to do it manually when the user saves the new question*/
                  setFieldTouched(
                    "matchChoices",
                    [...new Array(values.matchChoices.length)].map(() => ({
                      matchChoice: true
                    }))
                  );
              });
              submitForm();
            }}
            open={open}
            onClose={onClose}
            onQuestionTypeChange={newQuestionType => {
              setQuestionType(newQuestionType);
              setFieldValue("questionType", newQuestionType);
            }}
            loading={loading}
            isSurveyQuestion={isSurveyQuestion}
            onAnswersChange={answers => setFieldValue("answers", [...answers])}
            questionFilesList={filesList}
            setQuestionFilesList={setFilterList}
          />
          {showLoadingScreen && <LoadingSpinnerWithBackdrop />}
        </Form>
      )}
    </Formik>
  );
};

EditQuestion.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create the quiz/survey
   */
  onSubmit: PropTypes.func,
  /**
   * The type of question. If question is an existing question being edited and not
   * a new question, newQuestionType will be undefined
   */
  newQuestionType: PropTypes.oneOf(questionTypeValues),
  /**
   * Will be undefined if a new question is being created
   */
  existingQuestionData: PropTypes.object,
  loading: PropTypes.bool,
  isSurveyQuestion: PropTypes.bool,
  quizId: PropTypes.string,
  surveyId: PropTypes.string
};

export default EditQuestion;
